import { BusinessObjectBase } from './base';
import { processDataValueSchema } from '@schemas/business-objects/process-data-value';
import type { ProcessDataType } from './process-data-type';
import type { Id } from '../base-types';

export interface ProcessDataValueData {
  readonly id: Id;
  readonly name?: string;
  readonly stringValue?: string;
  readonly numberValue?: number;
  readonly booleanValue?: boolean;
  readonly sequence?: number;
  readonly dataTypeId: Id;
  readonly dataType?: ProcessDataType;
}

export interface ProcessDataValue extends Readonly<ProcessDataValueData> {}
type ProcessDataValueCreateWithChildrenData = Partial<ProcessDataValueData>;

export class ProcessDataValue extends BusinessObjectBase {
  static readonly entityName = 'ProcessDataValue' as const;
  static readonly plural = 'process-data-values';
  static schema = processDataValueSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDataValue[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDataValue(data));
  }

  static async findOne(filter?: any): Promise<ProcessDataValue | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDataValue(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessDataValue> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDataValue(data);
  }

  static async create(
    data: Omit<ProcessDataValueData, 'id'>,
  ): Promise<ProcessDataValue> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDataValue(result);
  }

  static async createMany(
    data: Omit<ProcessDataValueData, 'id'>[],
  ): Promise<ProcessDataValue[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDataValue(item));
  }

  static async createWithChildren(
    data: ProcessDataValueCreateWithChildrenData,
  ): Promise<ProcessDataValue> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessDataValue(result);
  }

  static async createManyWithChildren(
    data: ProcessDataValueCreateWithChildrenData[],
  ): Promise<ProcessDataValue[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDataValue(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessDataValueData>,
  ): Promise<ProcessDataValue> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessDataValue(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
